// La sesión del Catálogo (ADR-2026-10-13-catalogo-con-el-agente,
// ADR-2026-10-14-el-catalogo-arranca-con-el-agente-a-pantalla-completa,
// ADR-2026-10-14-el-catalogo-conversa-a-pantalla-completa-hasta-armar). Dos fases:
//  • "conversando": mientras no arrancan los prompts (aunque el agente ya haya propuesto las
//    tomas y espere la confirmación), la conversación va a pantalla completa: el agente mira los
//    productos y pregunta lo que haga falta.
//  • "catalogo": cuando el escritor arranca con los prompts, la conversación se achica al panel
//    del costado (view transition) y entra el catálogo.
// En las dos la sidebar va a la tira (sólo el logo). En el catálogo el agente vive en el panel
// del costado y a la izquierda está el catálogo: el título, la
// configuración plegada (formato, calidad, estilo y tomas), un bloque por producto con un cuadro
// por toma, y el pie con "Modificar catálogo" (abre o cierra el agente) y Generar. Mientras el
// agente arma el plan, los bloques muestran el lugar de los cuadros; cada foto aparece en su
// cuadro apenas llega y tocarla la abre en el visor.
// La sesión ES un chat (tipo `catalogo`): al entrar se carga y, si el plan lo pide, se le manda
// el pedido de la portada EN SECUENCIA (cargar → confirmar que es este chat → mandar).
import { Loader2, MessageCircle, RefreshCw, Sparkles, X } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { useEntornoChatAtlas, useRegistrarPantalla, type ControladorDePantalla } from '../../../../chat-atlas';
import { useChatAtlas, useChatAtlasCascaron } from '../../../../chat-atlas/ChatAtlasContext';
import { ConversacionChatAtlas } from '../../../../chat-atlas/ui/ConversacionChatAtlas';
import CachedImg from '../../../../components/CachedImg';
import { CumbreMascot } from '../../../../components/CumbreMascot';
import { CreditBadge } from '../../../../components/CreditBadge';
import { EncabezadoPantalla } from '../../../../components/PantallaChrome';
import { useSidebar } from '../../../../context/SidebarContext';
import { useDebajoDeMd } from '../../../../hooks/useEsMobile';
import { GeneratedImagesHistory } from '../../../GeneratedImagesHistory';
import { adjuntoPorId } from '../../adjuntosDelMensaje';
import { useAgentEnv } from '../../agentEnvContext';
import { useTandasPanel } from '../../useGeneracionesEnCurso';
import { preloadGeneratedImages } from '../../../../utils/preloadGeneratedImages';
import { describirCatalogo, PANTALLA as PANTALLA_DEL_CATALOGO } from './agente/describir';
import { ejecutarHerramientaDelCatalogo } from './agente/ejecutar';
import { CLAVE_PANTALLA_CATALOGO, HERRAMIENTAS_DEL_CATALOGO, SKILL_DE_CATALOGO } from './agente/herramientas';
import { CajaDelAgenteMobile, HojaDelAgente, PastillaDelAgente, type EstadoDelAgenteMobile } from './AgenteDelCatalogoMobile';
import { BORRADOR_VACIO, borradorDespuesDeEnviar, restaurarBorrador, type BorradorDelAgente } from './borradorDelAgente';
import { BloqueDeProducto } from './BloqueDeProducto';
import { buscarProductosParaElPlan, fuentesDeProductos } from './buscarProductos';
import { ConfiguracionDelCatalogo, TituloEditable } from './ConfiguracionDelCatalogo';
import { PanelDeItem } from './PanelDeItem';
import type { EstadoDeLaPortada, PrimerMensaje } from './PortadaDeCatalogo';
import { aspectDelFormato, costoPorLanzar, imagenesEnCurso, imagenesPorLanzar, itemsPendientesDeEscritura, planVacio, queFalta, type ItemDelPlan, type PlanDeCatalogo } from './plan';
import { RUTA_CATALOGO } from './rutas';
import './transicionDelCatalogo.css';
import { useMiniaturas } from './useMiniaturas';
import { useTiraDeSidebar } from './useTiraDeSidebar';
import { fotosDelCatalogo } from './fotosDelCatalogo';
import { estiloDelMarcoTelefono, MARCO_TELEFONO } from './marcoDelTelefono';
import { alTocarLaPastilla, textoDeLaPastilla } from './pastillaDelAgente';
import { SalidasDelCatalogo } from './SalidasDelCatalogo';
import { usePlanDeSesion } from './usePlanDeSesion';

export const PANTALLA_CATALOGO = 'el modo Catálogo';
export const NOMBRE_TRANSICION_PLAN = 'plan-del-catalogo';
export const NOMBRE_TRANSICION_CONVERSACION = 'conversacion-del-catalogo';
const PEDIDO_GENERICO = 'Mirá los productos de este catálogo y proponeme las tomas.';
/** Cuánto se espera a que el agente dé señales antes de ofrecer volver a intentar. */
const ESPERA_ANTES_DE_REINTENTAR_MS = 15_000;

const dormir = (ms: number) => new Promise<void>((r) => window.setTimeout(r, ms));

const pideMenosMovimiento = (): boolean => {
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
};

type Fase = 'conversando' | 'catalogo';

/** El catálogo ya tiene algo escrito o generado: hay qué mostrar. */
const conPrompts = (plan: PlanDeCatalogo): boolean =>
  plan.productos.some((p) => p.items.some((i) => !!i.prompt.trim() || i.estado === 'generando' || i.estado === 'generada' || (i.imageIds?.length ?? 0) > 0));

export const SesionDeCatalogo: React.FC<{ sesionId: string }> = ({ sesionId }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const chat = useChatAtlas();
  const chatRef = useRef(chat);
  chatRef.current = chat;
  const { abierto: panelAbierto, abrirChat, cerrarChat, fijarContexto } = useChatAtlasCascaron();
  const { companyId, brandId } = useAgentEnv();
  const tandas = useTandasPanel(companyId, 'catalogo-agente');
  const tandaApi = useMemo(
    () => ({ crearTanda: tandas.crearTanda, registrarExecutionIds: tandas.registrarExecutionIds, cerrarTanda: tandas.cerrarTanda, fallarTanda: tandas.fallarTanda }),
    [tandas.crearTanda, tandas.registrarExecutionIds, tandas.cerrarTanda, tandas.fallarTanda],
  );
  // El plan que la portada acaba de guardar: así la sesión se dibuja en el primer frame de la
  // transición, sin esperar a leerlo.
  const [estadoDeLaPortada] = useState(() => location.state as Partial<EstadoDeLaPortada> | null);
  const sesion = usePlanDeSesion({ chatId: sesionId, companyId, tanda: tandaApi, planInicial: estadoDeLaPortada?.plan ?? null });
  const { plan, acciones, setPlan } = sesion;
  // En el teléfono el agente del catálogo no es el panel del costado (ahí sería pantalla completa
  // tapando el catálogo): es una pastilla abajo, la caja sobre el catálogo y una hoja que sube
  // (ADR-2026-10-15-el-catalogo-en-el-telefono).
  const esTelefono = useDebajoDeMd();
  const [agenteMobile, setAgenteMobile] = useState<EstadoDelAgenteMobile>('pastilla');
  // Lo escrito en la caja sobrevive a ver la conversación o cerrarla; se vacía al mandar.
  const [borradorMobile, setBorradorMobile] = useState<BorradorDelAgente>(BORRADOR_VACIO);
  const [avisoMobile, setAvisoMobile] = useState<string | null>(null);

  // El panel del agente se agranda EN SU LUGAR, sobre el catálogo, y el mismo botón lo vuelve a
  // achicar: ir a la pantalla del Agente (`/chat`) sacaba del catálogo sin camino de vuelta
  // (ADR-2026-10-15-agrandar-el-agente-del-catalogo-en-su-lugar). Al salir se suelta el
  // contexto, para que el panel de las otras pantallas vuelva a expandir como siempre.
  const tituloDelPlan = plan?.titulo;
  useEffect(() => {
    fijarContexto({
      pantalla: PANTALLA_DEL_CATALOGO,
      ruta: location.pathname,
      ...(tituloDelPlan ? { detalle: `el catálogo «${tituloDelPlan}»` } : {}),
      agrandarEnElLugar: true,
    });
  }, [fijarContexto, location.pathname, tituloDelPlan]);
  useEffect(() => () => fijarContexto(null), [fijarContexto]);

  // ── La fase: la conversación a pantalla completa hasta que el agente arma el catálogo ──
  const [fase, setFase] = useState<Fase | null>(null);
  useEffect(() => {
    if (fase === null && plan) setFase(conPrompts(plan) ? 'catalogo' : 'conversando');
  }, [fase, plan]);
  // La sidebar va a la tira en toda la sesión: en la conversación y en el catálogo.
  const corrimiento = useTiraDeSidebar(fase !== null);
  // Conversando, la conversación está en la pantalla: el panel del costado no va. En el
  // catálogo el agente vive al costado (también entrando desde "Mis catálogos" o la URL).
  useEffect(() => {
    if (fase === 'conversando' && panelAbierto) cerrarChat();
    if (fase === 'catalogo' && !panelAbierto && !esTelefono) abrirChat();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fase]);
  // En el teléfono, el panel del chat (pantalla completa) nunca tapa el catálogo: el agente es la hoja.
  useEffect(() => {
    if (esTelefono && fase === 'catalogo' && panelAbierto) cerrarChat();
  }, [esTelefono, fase, panelAbierto, cerrarChat]);
  // Proponer las tomas no alcanza: el catálogo aparece cuando arrancan los prompts (pedido de
  // Matías: si el agente propone y pide confirmación, se sigue en la conversación).
  // ── Volver: la pastilla de la tira o salir de la sesión ─────────────────
  // (ADR-2026-10-14-salir-del-catalogo-expande-la-sidebar). Tocar la pastilla abre la sidebar
  // entera: el panel del agente se oculta. Salir (atrás del navegador, las migas) cierra el panel
  // del chat de esta sesión y deja la sidebar entera, no en la tira ni colapsada.
  const { isCollapsed: sidebarColapsada, pedirExpandirSidebar } = useSidebar();
  const sidebarColapsadaAntes = useRef(sidebarColapsada);
  useEffect(() => {
    const seAbrio = sidebarColapsadaAntes.current && !sidebarColapsada;
    sidebarColapsadaAntes.current = sidebarColapsada;
    if (seAbrio && panelAbierto) cerrarChat();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sidebarColapsada]);
  const alSalirRef = useRef<() => void>(() => {});
  alSalirRef.current = () => {
    if (panelAbierto && chatRef.current.motor.getChatId() === sesionId) cerrarChat();
    pedirExpandirSidebar();
  };
  // Diferido un tick: el doble montaje de StrictMode desmonta y vuelve a montar al instante, y
  // el remontaje lo cancela (los refs sobreviven); una salida de verdad no vuelve.
  const salidaPendiente = useRef<number | null>(null);
  useEffect(() => {
    if (salidaPendiente.current !== null) {
      window.clearTimeout(salidaPendiente.current);
      salidaPendiente.current = null;
    }
    return () => {
      salidaPendiente.current = window.setTimeout(() => alSalirRef.current(), 0);
    };
  }, []);

  const armado = !!plan && (sesion.escribiendo || conPrompts(plan));
  useEffect(() => {
    if (fase !== 'conversando' || !armado) return;
    const pasar = () => {
      setFase('catalogo');
      if (!esTelefono) abrirChat();
    };
    const doc = document as Document & { startViewTransition?: (cb: () => void) => unknown };
    if (doc.startViewTransition && !pideMenosMovimiento()) doc.startViewTransition(() => flushSync(pasar));
    else pasar();
  }, [fase, armado, abrirChat, esTelefono]);

  // ── La sesión es el chat ──────────────────────────────────────────────────
  const esElChat = chat.motor.getChatId() === sesionId;
  const cargarChat = chat.cargarChat;
  useEffect(() => {
    if (chat.motor.getChatId() === sesionId) return;
    void cargarChat(sesionId)
      .then(() => {
        if (chat.motor.getChatId() !== sesionId) {
          navigate(RUTA_CATALOGO, { replace: true, state: { aviso: 'Esa sesión no existe o no es de tu empresa.' } });
        }
      })
      .catch(() => navigate(RUTA_CATALOGO, { replace: true, state: { aviso: 'No se pudo abrir la sesión. Intentá de nuevo.' } }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sesionId]);

  /** Espera (hasta ~4 s) a que el motor tenga ESTE chat cargado y renderizado; si no, lo carga. */
  const asegurarElChat = useCallback(async (): Promise<boolean> => {
    for (let i = 0; i < 40 && chatRef.current.motor.getChatId() !== sesionId; i++) await dormir(100);
    if (chatRef.current.motor.getChatId() !== sesionId) await cargarChat(sesionId);
    await dormir(50); // un render, para leer los mensajes del chat recién cargado
    return chatRef.current.motor.getChatId() === sesionId;
  }, [cargarChat, sesionId]);

  const adjuntosDeLaPortada = useRef<PrimerMensaje['adjuntos']>(estadoDeLaPortada?.primerMensaje?.adjuntos ?? []);
  const mandar = useCallback(
    async (texto: string, adjuntos: PrimerMensaje['adjuntos'] = []): Promise<boolean> => {
      if (!(await asegurarElChat())) {
        console.warn('[catalogo] no se pudo cargar el chat de la sesión para mandar el mensaje', sesionId);
        return false;
      }
      // Los adjuntos van CON el mensaje (no `agregarAdjunto` + enviar: el envío lee el ref y
      // el estado todavía no se aplicó, y el mensaje salía sin ellos).
      // Con un turno en curso el motor descarta el mensaje: va a la cola y sale al terminar.
      if (chatRef.current.streaming) return chatRef.current.encolarMensaje(texto, adjuntos);
      chatRef.current.enviarMensaje(texto, adjuntos);
      return true;
    },
    [asegurarElChat, sesionId],
  );

  // ── El pedido de la portada llega al agente solo, una vez ────────────────
  // El plan guarda `pedidoEnviado`; el ref cubre el doble efecto de StrictMode (se libera en
  // la limpieza si todavía no se mandó, para que la segunda corrida lo haga).
  const pedidoEnCursoRef = useRef(false);
  const pedidoListo = !!plan && !plan.pedidoEnviado && !!plan.pedido.trim() && plan.tomas.length === 0;
  useEffect(() => {
    if (!pedidoListo || pedidoEnCursoRef.current || !plan) return;
    pedidoEnCursoRef.current = true;
    let cancelado = false;
    let mandado = false;
    void (async () => {
      if (!(await asegurarElChat()) || cancelado) {
        if (!cancelado) console.warn('[catalogo] no se pudo cargar el chat de la sesión', sesionId);
        return;
      }
      // Un chat que ya tiene mensajes ya recibió el pedido (otra pestaña, una versión vieja).
      if (chatRef.current.mensajes.length > 0) {
        mandado = true;
        setPlan((p) => ({ ...p, pedidoEnviado: true }));
        return;
      }
      mandado = true;
      setPlan((p) => ({ ...p, pedidoEnviado: true }));
      if (location.state) navigate(location.pathname, { replace: true, state: null });
      const adjuntos = adjuntosDeLaPortada.current;
      adjuntosDeLaPortada.current = [];
      chatRef.current.enviarMensaje(plan.pedido, adjuntos);
      console.info('[catalogo] pedido enviado al agente', sesionId);
    })();
    return () => {
      cancelado = true;
      if (!mandado) pedidoEnCursoRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pedidoListo]);

  // ── Lo que el agente está haciendo ───────────────────────────────────────
  const agenteTrabajando = esElChat && chat.streaming;
  const tarjetaPendiente = esElChat ? chat.motor.activeToolCard : null;
  // En el catálogo, si el agente pregunta algo y el panel está cerrado, se abre solo.
  // En el teléfono no se abre sola: la pastilla avisa que hay algo para leer.
  useEffect(() => {
    if (!esTelefono && fase === 'catalogo' && tarjetaPendiente && !panelAbierto) abrirChat();
  }, [esTelefono, fase, tarjetaPendiente, panelAbierto, abrirChat]);
  // Lo que la persona ya leyó: los mensajes que había la última vez que la hoja estuvo abierta (o
  // al entrar). Uno más, con el agente quieto, es una respuesta sin leer.
  const vistosRef = useRef<number | null>(null);
  useEffect(() => {
    if (!esElChat) return;
    if (vistosRef.current === null || agenteMobile === 'chat') vistosRef.current = chat.mensajes.length;
  });
  const novedadMobile = esElChat && !agenteTrabajando && (!!tarjetaPendiente || chat.mensajes.length > (vistosRef.current ?? chat.mensajes.length));
  // Sin tomas, sin mensajes y sin turno después de un rato: el pedido no arrancó.
  const sinArrancar = !!plan && plan.tomas.length === 0 && !agenteTrabajando && (!esElChat || chat.mensajes.length === 0);
  const [ofrecerReintento, setOfrecerReintento] = useState(false);
  useEffect(() => {
    if (!sinArrancar) {
      setOfrecerReintento(false);
      return;
    }
    const t = window.setTimeout(() => setOfrecerReintento(true), ESPERA_ANTES_DE_REINTENTAR_MS);
    return () => window.clearTimeout(t);
  }, [sinArrancar]);

  const reescribirConElAgente = useCallback(
    (producto: string, toma: string) => {
      acciones.abrirItem(null);
      const texto = `Reescribí el prompt de ${producto} · ${toma}: `;
      if (esTelefono) {
        chatRef.current.setBorrador(texto);
        setAgenteMobile('chat');
        return;
      }
      abrirChat({ mensajeInicial: texto });
    },
    [acciones, abrirChat, esTelefono],
  );

  // ── El puente con el agente (ADR-2026-10-14-el-agente-arma-el-catalogo-desde-la-pantalla) ──
  // Las herramientas leen el plan del hook (`sesion.leerPlan`), que se actualiza al instante:
  // un ref propio de acá se ponía al día recién en el render siguiente y `proponer_plan`, que
  // reemplaza las tomas y lee el plan en el mismo tick, le contestaba "0 tomas" al agente
  // (ADR-2026-10-15-las-herramientas-leen-el-plan-vivo).
  const { escribiendo, generando, leerPlan, productosSinGuardar } = sesion;
  const planVivo = useCallback(() => leerPlan() ?? planVacio([], [], ''), [leerPlan]);
  // `sumar_productos` (ADR-2026-10-15-el-agente-suma-productos-al-catalogo): el catálogo de la
  // marca que el chat ya tiene, y la base para lo que todavía no llegó ahí.
  const { productos: productosDeLaMarca } = useEntornoChatAtlas();
  const productosDeLaMarcaRef = useRef(productosDeLaMarca);
  productosDeLaMarcaRef.current = productosDeLaMarca;
  const buscarProductos = useCallback(
    (ids: string[]) =>
      companyId
        ? buscarProductosParaElPlan(ids, productosDeLaMarcaRef.current, fuentesDeProductos(companyId))
        : Promise.resolve({ encontrados: [], noEncontrados: ids }),
    [companyId],
  );
  // `ver_tomas`: las fotos de las tarjetas, firmadas en su tamaño chico (opt), para el modelo.
  const firmarFotos = useCallback(
    async (ids: string[]) => {
      const mapa = new Map<string, string>();
      if (!companyId || ids.length === 0) return mapa;
      for (const f of await preloadGeneratedImages(ids, companyId)) {
        const url = f.optSignedUrl || f.signedUrl;
        if (url) mapa.set(String(f.id), url);
      }
      return mapa;
    },
    [companyId],
  );
  // `modificar_plan` op referencia: una foto que la persona subió a mitad del chat (una plantilla
  // de la marca) se suma al plan por su adjunto_id (ADR-2026-10-15-la-plantilla-de-marca).
  const adjuntoDelChat = useCallback((id: string) => {
    const a = adjuntoPorId(chatRef.current.motor.getChatId() === sesionId ? chatRef.current.mensajes : [], id);
    return a ? { storagePath: a.storagePath, ...(a.bucket ? { bucket: a.bucket } : {}) } : null;
  }, [sesionId]);
  useRegistrarPantalla(
    useMemo<ControladorDePantalla>(
      () => ({
        clave: CLAVE_PANTALLA_CATALOGO,
        skill: SKILL_DE_CATALOGO,
        describir: () => describirCatalogo({ ruta: location.pathname, plan: leerPlan(), enSesion: true, escribiendo, generando, sinGuardar: productosSinGuardar }),
        aplicar: () => ({ aplicados: [], fallidos: [] }),
        herramientas: HERRAMIENTAS_DEL_CATALOGO,
        ejecutar: (nombre, input) =>
          ejecutarHerramientaDelCatalogo(
            nombre,
            input,
            { plan: planVivo() },
            { ...acciones, leerPlan: planVivo, buscarProductos, firmarFotos, adjuntoDelChat },
          ),
      }),
      [location.pathname, escribiendo, generando, productosSinGuardar, acciones, leerPlan, planVivo, buscarProductos, firmarFotos, adjuntoDelChat],
    ),
  );

  // ── Generar ───────────────────────────────────────────────────────────────
  const [error, setError] = useState<string | null>(null);
  const [sinCreditos, setSinCreditos] = useState(false);
  const generar = useCallback(
    async (soloItems?: string[]) => {
      setError(null);
      setSinCreditos(false);
      const r = await acciones.generar(soloItems);
      if (!r.ok) {
        setError(r.error);
        setSinCreditos(r.sinCreditos);
      }
    },
    [acciones],
  );

  // ── Cada foto en su cuadro, apenas llega ─────────────────────────────────
  const idsGenerados = useMemo(() => (plan ? plan.productos.flatMap((p) => p.items.flatMap((i) => i.imageIds ?? [])) : []), [plan]);
  const miniaturas = useMiniaturas(idsGenerados, companyId);
  const miniaturaDe = useCallback((item: ItemDelPlan) => (item.imageIds?.[0] ? (miniaturas.get(item.imageIds[0]) ?? null) : null), [miniaturas]);
  // La foto ampliada: el visor de las creaciones, con las flechas en el orden del plan.
  const [imagenAbierta, setImagenAbierta] = useState<string | null>(null);
  const ordenDelVisor = useMemo(
    () => (plan ? plan.productos.flatMap((p) => p.items.flatMap((i) => (i.imageIds?.[0] ? [i.imageIds[0]] : []))) : []),
    [plan],
  );

  if (sesion.noExiste) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 px-8 text-center">
        <p className="text-[16px] font-semibold text-brand-black">Esta sesión no tiene un catálogo</p>
        <button type="button" onClick={() => navigate(RUTA_CATALOGO)} className="rounded-md bg-brand-primary py-2.5 px-5 text-[15px] font-semibold text-white hover:bg-brand-primary-hover">
          Empezar uno
        </button>
      </div>
    );
  }
  if (!plan || fase === null) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-[#3C3C43]/50" aria-hidden="true" />
      </div>
    );
  }

  const faltas = queFalta(plan);
  const faltaDe = (varianteId: string) => faltas.find((f) => f.startsWith(`${plan.productos.find((p) => p.varianteId === varianteId)?.nombre}:`)) ?? null;
  const pendientes = itemsPendientesDeEscritura(plan).length;
  const total = imagenesPorLanzar(plan);
  const enCurso = imagenesEnCurso(plan);
  const costo = costoPorLanzar(plan);
  const sinTomas = plan.tomas.length === 0;
  const hayFotos = fotosDelCatalogo(plan).length > 0;
  const muestraGenerar = total > 0 || sesion.generando || sesion.generarPedido;
  const estadoDeLaPastilla = { trabajando: agenteTrabajando, actividad: chat.actividad, pregunta: !!tarjetaPendiente, novedad: novedadMobile };
  const puedeGenerar = faltas.length === 0 && total > 0 && !sesion.generando && !sesion.escribiendo;
  const aspecto = aspectDelFormato(plan.formato).replace(':', ' / ');
  const itemAbierto = sesion.itemAbierto
    ? plan.productos.flatMap((p) => p.items.filter((i) => i.id === sesion.itemAbierto).map((i) => ({ producto: p, item: i, toma: plan.tomas.find((t) => t.id === i.tomaId) })))[0]
    : null;

  // Los datos de un producto que no llegaron a Mi marca (review 2026-10-15): siguen en este
  // catálogo, pero otro catálogo con ese producto no los tendría.
  const nombresSinGuardar = plan.productos.filter((p) => productosSinGuardar.includes(p.varianteId)).map((p) => p.nombre);
  const avisoSinGuardar = nombresSinGuardar.length > 0 && (
    <div className="flex items-center justify-between gap-4 rounded-2xl bg-[#FFF8EB] py-3 px-4 text-[13.5px] text-brand-black">
      <span>
        No se pudieron guardar en Mi marca los datos de {nombresSinGuardar.join(', ')}. En este catálogo siguen.
      </span>
      <button
        type="button"
        onClick={acciones.reintentarGuardado}
        className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-white py-1.5 px-3.5 text-[13px] font-semibold text-brand-black shadow-sm ring-1 ring-black/[0.06] hover:bg-black/[0.03]"
      >
        <RefreshCw className="h-3.5 w-3.5" strokeWidth={2.25} aria-hidden="true" />
        Volver a intentar
      </button>
    </div>
  );
  const avisoDeReintento = ofrecerReintento && (
    <div className="flex items-center justify-between gap-4 rounded-2xl bg-[#FFF8EB] py-3 px-4 text-[13.5px] text-brand-black">
      <span>El agente todavía no arrancó con tu pedido.</span>
      <button
        type="button"
        onClick={() => {
          setOfrecerReintento(false);
          void mandar(plan.pedido.trim() || PEDIDO_GENERICO, adjuntosDeLaPortada.current);
        }}
        className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-white py-1.5 px-3.5 text-[13px] font-semibold text-brand-black shadow-sm ring-1 ring-black/[0.06] hover:bg-black/[0.03]"
      >
        <RefreshCw className="h-3.5 w-3.5" strokeWidth={2.25} aria-hidden="true" />
        Volver a intentar
      </button>
    </div>
  );

  // ── Conversando: la conversación del agente a pantalla completa ──────────
  if (fase === 'conversando') {
    const conFoto = plan.productos.filter((p) => p.portadaUrl);
    // Hasta que el chat de la sesión tiene su primer mensaje, la conversación estaría vacía y
    // mostraría el arranque genérico del agente: en su lugar, Cumbre leyendo el pedido.
    const sinMensajes = !esElChat || (chat.mensajes.length === 0 && !chat.streaming);
    return (
      <div className={`relative flex h-[100dvh] flex-col overflow-hidden bg-white ${MARCO_TELEFONO}`} style={{ viewTransitionName: NOMBRE_TRANSICION_CONVERSACION, ...estiloDelMarcoTelefono(esTelefono) }}>
        <div className="flex shrink-0 items-center gap-3 pr-6 pt-5" style={{ paddingLeft: corrimiento + 24 }}>
          <span className="flex shrink-0 items-center -space-x-2" aria-hidden="true">
            {conFoto.slice(0, 4).map((p, i) => (
              <span key={p.varianteId} className="block h-8 w-8 overflow-hidden rounded-lg bg-white shadow-sm ring-2 ring-white" style={{ zIndex: 4 - i }}>
                <CachedImg src={p.portadaUrl as string} alt="" className="h-full w-full object-cover" />
              </span>
            ))}
          </span>
          <p className="min-w-0 truncate text-[14px] font-semibold text-brand-black">
            {plan.titulo}
            <span className="font-normal text-[#3C3C43]/60">
              {' · '}
              {plan.productos.length} producto{plan.productos.length === 1 ? '' : 's'}
            </span>
          </p>
        </div>
        {avisoDeReintento && <div className="mx-auto w-full max-w-3xl shrink-0 px-6 pt-3">{avisoDeReintento}</div>}
        <div className="flex min-h-0 flex-1 flex-col">
          {sinMensajes ? (
            <div className="flex flex-1 flex-col items-center justify-center gap-3">
              <CumbreMascot state="thinking" size={88} decorativa />
              <p className="text-[14px] text-[#3C3C43]/60">Leyendo tu pedido…</p>
            </div>
          ) : (
            <ConversacionChatAtlas variante="pantalla" />
          )}
        </div>
      </div>
    );
  }

  const estadoDelPie = error ? (
    <span className="text-[#B42318]">
      {error}
      {sinCreditos && (
        <button type="button" onClick={() => navigate('/subscription')} className="ml-2 font-semibold text-brand-primary hover:underline">
          Sumar créditos
        </button>
      )}
    </span>
  ) : sinTomas ? (
    agenteTrabajando ? (chat.actividad ?? 'El agente está armando el catálogo…') : 'El agente va a proponer las tomas.'
  ) : faltas.length > 0 ? (
    `Falta: ${faltas[0]}${faltas.length > 1 ? ` (+${faltas.length - 1})` : ''}`
  ) : total === 0 ? (
    enCurso > 0 ? `${enCurso} imagen${enCurso === 1 ? '' : 'es'} en curso; nada más por lanzar.` : 'Todo generado. Pedile cambios al agente o rehacé una toma desde su cuadro.'
  ) : (
    `${total} imagen${total === 1 ? '' : 'es'} por lanzar${enCurso > 0 ? ` · ${enCurso} en curso` : ''} · ${plan.productos.length} producto${plan.productos.length === 1 ? '' : 's'} · ${plan.tomas.length} toma${plan.tomas.length === 1 ? '' : 's'}`
  );

  return (
    // Con la tira, el logo flota arriba a la izquierda: la columna del catálogo se corre lo que mide.
    <div className={`relative flex h-[100dvh] flex-col overflow-hidden bg-white ${MARCO_TELEFONO}`} style={{ viewTransitionName: NOMBRE_TRANSICION_PLAN, paddingLeft: corrimiento, ...estiloDelMarcoTelefono(esTelefono) }}>
      <div className="min-h-0 flex-1 overflow-y-auto">
        {/* En el teléfono, aire abajo para que la barra flotante del agente no tape el último producto. */}
        <div className="mx-auto flex w-full max-w-[1100px] flex-col gap-4 px-4 pb-32 md:px-6 md:pb-6">
          <div className="pt-1">
            <EncabezadoPantalla
              migas={[{ label: 'Inicio', onClick: () => navigate('/interfaz-v2') }, { label: 'Catálogo', onClick: () => navigate(RUTA_CATALOGO) }, { label: plan.titulo }]}
              className="!px-0 !pb-1 hidden md:block"
            />
            <TituloEditable valor={plan.titulo} onGuardar={acciones.setTitulo} />
          </div>
          <ConfiguracionDelCatalogo
            plan={plan}
            escribiendo={sesion.escribiendo}
            pendientesDeEscritura={pendientes}
            onEstilo={(e, reescribir) => acciones.setEstilo(e, reescribir ? { reescribir: true } : undefined)}
            onFormato={acciones.setFormato}
            onResolucion={acciones.setResolucion}
            onQuitarToma={acciones.quitarToma}
            onEscribir={() => void acciones.escribir()}
          />
          {avisoDeReintento}
          {avisoSinGuardar}
          {plan.productos.length === 0 && <p className="text-[14px] text-[#3C3C43]/60">No quedó ningún producto en el catálogo.</p>}
          {plan.productos.map((producto, i) => (
            <div key={producto.varianteId} className="animate-fade-in" style={{ animationDelay: `${Math.min(i, 8) * 60}ms` }}>
              <BloqueDeProducto
                producto={producto}
                tomas={plan.tomas}
                falta={faltaDe(producto.varianteId)}
                itemAbierto={sesion.itemAbierto}
                miniaturaDe={miniaturaDe}
                aspecto={aspecto}
                onAbrirItem={acciones.abrirItem}
                onVerImagen={setImagenAbierta}
                onQuitarPendiente={(id) => acciones.quitarPendientes({ items: [id] })}
                onMedidas={(m) => acciones.setMedidas(producto.varianteId, m)}
                onFicha={(f) => acciones.setFicha(producto.varianteId, f)}
                onNotas={(n) => acciones.setNotas(producto.varianteId, n)}
                onQuitar={() => acciones.quitarProducto(producto.varianteId)}
                escribiendo={sesion.escribiendo}
              />
            </div>
          ))}
        </div>
      </div>

      <div className="hidden shrink-0 border-t border-black/[0.06] bg-white md:block">
        <div className="mx-auto flex w-full max-w-[1100px] items-center justify-between gap-4 px-6 py-4">
          <p className="min-w-0 truncate text-[13px] text-[#3C3C43]/60">{estadoDelPie}</p>
          <div className="flex shrink-0 items-center gap-2">
            <SalidasDelCatalogo plan={plan} companyId={companyId} brandId={brandId ?? null} />
            <button
              type="button"
              onClick={() => (panelAbierto ? cerrarChat() : abrirChat())}
              aria-pressed={panelAbierto}
              className={`inline-flex items-center gap-2 rounded-xl border py-3 px-6 text-[15px] font-semibold transition-colors ${
                panelAbierto ? 'border-brand-primary bg-[#E9EDFD] text-brand-primary' : 'border-black/[0.1] bg-white text-brand-black hover:bg-black/[0.03]'
              }`}
            >
              {agenteTrabajando ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <MessageCircle className="h-4 w-4" strokeWidth={2} aria-hidden="true" />}
              Modificar catálogo
            </button>
            {sesion.generarPedido && (
              <button type="button" onClick={acciones.cancelarGenerar} className="py-2 px-4 text-[14px] font-semibold text-[#3C3C43]/70 hover:text-[#02062B]">
                No generar
              </button>
            )}
            <button
              type="button"
              onClick={() => void generar()}
              disabled={!puedeGenerar}
              title={faltas[0]}
              className={`inline-flex items-center gap-2 rounded-xl bg-brand-primary py-3 px-6 text-[15px] font-semibold text-white shadow-sm transition-colors hover:bg-brand-primary-hover disabled:cursor-not-allowed disabled:opacity-50 ${
                sesion.generarPedido ? 'ring-4 ring-[rgb(var(--brand-primary-rgb)_/_0.25)]' : ''
              }`}
            >
              {sesion.generando && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
              {sesion.generando ? 'Generando…' : `Generar ${total} imagen${total === 1 ? '' : 'es'}`}
              {!sesion.generando && <CreditBadge credits={costo} tone="on-primary" size="sm" />}
            </button>
          </div>
        </div>
      </div>

      {/* El teléfono: la pastilla del agente y, según el contexto, Generar, Descargar y Lanzamiento
          como íconos (ADR-2026-10-15-el-catalogo-en-el-telefono). */}
      {esTelefono && (
        <div className="pointer-events-none absolute inset-x-0 bottom-0 md:hidden">
          {(error || (faltas.length > 0 && !sinTomas)) && (
            <p className={`mx-3 mb-2 truncate rounded-full bg-white/95 py-1.5 px-3 text-[12.5px] shadow-sm ring-1 ring-black/[0.06] backdrop-blur-md ${error ? 'text-[#B42318]' : 'text-[#3C3C43]/70'}`}>
              {error ?? `Falta: ${faltas[0]}`}
            </p>
          )}
          <div className="pointer-events-auto relative flex items-center gap-2 bg-gradient-to-t from-white via-white/85 to-transparent px-3 pt-4" style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 12px)' }}>
            <PastillaDelAgente
              texto={textoDeLaPastilla(estadoDeLaPastilla)}
              trabajando={agenteTrabajando}
              novedad={novedadMobile}
              onAbrir={() => setAgenteMobile(alTocarLaPastilla(estadoDeLaPastilla))}
              compacta={hayFotos && muestraGenerar}
            />
            <SalidasDelCatalogo plan={plan} companyId={companyId} brandId={brandId ?? null} soloIconos />
            {sesion.generarPedido && (
              <button
                type="button"
                onClick={acciones.cancelarGenerar}
                aria-label="No generar"
                className="grid h-12 w-12 shrink-0 place-items-center rounded-full bg-white text-[#3C3C43]/70 shadow-sm ring-1 ring-black/[0.06]"
              >
                <X className="h-5 w-5" strokeWidth={2} aria-hidden="true" />
              </button>
            )}
            {muestraGenerar && (
              <button
                type="button"
                onClick={() => void generar()}
                disabled={!puedeGenerar}
                aria-label={`Generar ${total} imagen${total === 1 ? '' : 'es'}`}
                className={`inline-flex h-12 shrink-0 items-center gap-1.5 rounded-full bg-brand-primary px-5 text-[15px] font-semibold text-white shadow-[0_8px_24px_-10px_rgba(22,68,235,0.6)] transition-transform active:scale-95 disabled:opacity-50 ${
                  sesion.generarPedido ? 'ring-4 ring-[rgb(var(--brand-primary-rgb)_/_0.25)]' : ''
                } ${hayFotos ? 'ml-auto' : ''}`}
              >
                {sesion.generando ? <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" /> : <Sparkles className="h-5 w-5" strokeWidth={2} aria-hidden="true" />}
                {sesion.generando ? 'Generando…' : 'Generar'}
                {!sesion.generando && <CreditBadge credits={costo} tone="on-primary" size="sm" />}
              </button>
            )}
          </div>
        </div>
      )}
      {esTelefono && agenteMobile === 'caja' && (
        <CajaDelAgenteMobile
          borrador={borradorMobile}
          onCambiarBorrador={setBorradorMobile}
          aviso={avisoMobile}
          onCerrar={() => {
            setAvisoMobile(null);
            setAgenteMobile('pastilla');
          }}
          onVerConversacion={() => setAgenteMobile('chat')}
          onEnviar={(texto, adjuntos) => {
            // Se vacía al mandar (reabrir la caja no lo manda dos veces); si no salió, vuelve
            // con un aviso (ADR-2026-10-16-el-borrador-de-la-caja-del-catalogo-sobrevive-3).
            const mandado = borradorMobile;
            setAvisoMobile(null);
            setBorradorMobile(borradorDespuesDeEnviar);
            setAgenteMobile('chat');
            void mandar(texto, adjuntos)
              .catch(() => false)
              .then((salio) => {
                if (salio) return;
                setBorradorMobile(restaurarBorrador(mandado));
                setAvisoMobile('No se pudo mandar. Probá de nuevo.');
                setAgenteMobile('caja');
              });
          }}
        />
      )}
      {esTelefono && <HojaDelAgente abierta={agenteMobile === 'chat'} onCerrar={() => setAgenteMobile('pastilla')} />}

      {itemAbierto?.toma && (
        <PanelDeItem
          key={itemAbierto.item.id}
          plan={plan}
          producto={itemAbierto.producto}
          toma={itemAbierto.toma}
          item={itemAbierto.item}
          companyId={companyId}
          generando={sesion.generando}
          onEditarPrompt={(p) => acciones.editarPrompt(itemAbierto.item.id, p)}
          onDesfijar={() => acciones.desfijar(itemAbierto.item.id)}
          onRehacer={() => void generar([itemAbierto.item.id])}
          onReescribirConElAgente={() => reescribirConElAgente(itemAbierto.producto.nombre, itemAbierto.toma!.titulo)}
          onQuitar={() => {
            acciones.abrirItem(null);
            acciones.quitarItem(itemAbierto.producto.varianteId, itemAbierto.item.tomaId);
          }}
          onCerrar={() => acciones.abrirItem(null)}
        />
      )}

      {imagenAbierta && (
        <GeneratedImagesHistory
          soloVisor={{
            imageId: imagenAbierta,
            orden: ordenDelVisor,
            brandId: brandId ?? null,
            onCerrar: () => setImagenAbierta(null),
            onError: () => setImagenAbierta(null),
          }}
        />
      )}
    </div>
  );
};
